import { useEffect, useState } from "react";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, User, IdCard, GraduationCap, Shirt, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getMyStudentDetail } from "@/lib/api/parent-students.functions";
import { PlayerAvatar } from "@/components/shared/PlayerAvatar";

export const Route = createFileRoute("/_authenticated/responsavel/alunos/$id")({
  head: () => ({
    meta: [
      { title: "Detalhes do Aluno | FutBux" },
      { name: "description", content: "Visualização completa do aluno vinculado ao seu perfil." },
      { property: "og:title", content: "Detalhes do Aluno | FutBux" },
      { property: "og:description", content: "Visualização completa do aluno vinculado ao seu perfil." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: Page,
});

const fmtDate = (d?: string | null) => (d ? new Date(d + "T00:00:00").toLocaleDateString("pt-BR") : "—");
const dash = (v?: any) => (v == null || v === "" ? "—" : String(v));

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-2 text-sm py-1 border-b last:border-b-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value}</span>
    </div>
  );
}

function Page() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const fn = useServerFn(getMyStudentDetail);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fn({ data: { studentId: id } });
        setData(res);
      } catch (e: any) {
        toast.error(e?.message || "Erro ao carregar aluno.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) return <div className="p-6 flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Carregando…</div>;
  if (!data?.student) return <div className="p-6 text-sm text-muted-foreground">Aluno não encontrado.</div>;

  const s = data.student;
  const p = data.parent || {};

  return (
    <div className="p-4 max-w-4xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={() => navigate({ to: "/responsavel/alunos" })}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
        </Button>
        <h1 className="text-2xl font-bold">{s.name}</h1>
        <Badge variant="outline" className="ml-2">{s.category || "Sem categoria"}</Badge>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* CARD 1 — Cadastro */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><User className="w-4 h-4 text-primary" /> Dados de Cadastro</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-center gap-3 pb-3 mb-2 border-b">
              {s.photo_url ? (
                <img src={s.photo_url} className="w-16 h-16 rounded-full object-cover border" alt={s.name} />
              ) : (
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center"><User className="w-6 h-6 text-muted-foreground" /></div>
              )}
              <div className="min-w-0">
                <p className="font-semibold truncate">{s.name}</p>
                <p className="text-xs text-muted-foreground">{s.training_centers?.name}</p>
              </div>
            </div>
            <Row label="CPF" value={dash(s.cpf)} />
            <Row label="Nascimento" value={fmtDate(s.birth_date)} />
            <Row label="Categoria" value={dash(s.category)} />
            <Row label="Sexo" value={dash(s.gender)} />
            <Row label="Goleiro" value={s.is_goalkeeper ? "Sim" : "Não"} />
            <Row label="Peso" value={s.weight ? `${s.weight} kg` : "—"} />
            <Row label="Altura" value={s.height ? `${s.height} m` : "—"} />
            <Row label="Status" value={<Badge variant={s.status === "active" ? "default" : "secondary"}>{s.status}</Badge>} />
          </CardContent>
        </Card>

        {/* CARD 2 — Responsável */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><IdCard className="w-4 h-4 text-primary" /> Responsável</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <Row label="Nome" value={dash(p.name)} />
            <Row label="CPF" value={dash(p.cpf)} />
            <Row label="Telefone" value={dash(p.phone)} />
            <Row label="E-mail" value={dash(p.email)} />
            <Row label="Nascimento" value={fmtDate(p.birth_date)} />
            <Row label="CEP" value={dash(p.cep)} />
            <Row label="Endereço" value={dash([p.street, p.number, p.complement].filter(Boolean).join(", ") || p.address)} />
            <Row label="Bairro" value={dash(p.neighborhood)} />
            <Row label="Cidade/UF" value={p.city || p.state ? `${dash(p.city)}/${dash(p.state)}` : "—"} />
          </CardContent>
        </Card>

        {/* CARD 3 — Currículo */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><GraduationCap className="w-4 h-4 text-primary" /> Currículo</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <Row label="Tipo de documento" value={dash(s.document_type)} />
            <Row
              label="Documento (frente)"
              value={s.document_front_url ? <a className="text-primary underline" href={s.document_front_url} target="_blank" rel="noreferrer">Abrir</a> : "—"}
            />
            <Row
              label="Documento (verso)"
              value={s.document_back_url ? <a className="text-primary underline" href={s.document_back_url} target="_blank" rel="noreferrer">Abrir</a> : "—"}
            />
            <Row
              label="Vídeo YouTube"
              value={s.youtube_url ? <a className="text-primary underline" href={s.youtube_url} target="_blank" rel="noreferrer">Abrir</a> : "—"}
            />
            <div className="pt-2">
              <p className="text-sm text-muted-foreground mb-1">Torneios</p>
              {Array.isArray(s.tournaments) && s.tournaments.length > 0 ? (
                <ul className="text-sm space-y-1">
                  {s.tournaments.map((t: any, i: number) => (
                    <li key={i} className="border rounded p-2">
                      <span className="font-medium">{t.name}</span>
                      {t.year ? ` · ${t.year}` : ""}
                      {t.result ? ` · ${t.result}` : ""}
                    </li>
                  ))}
                </ul>
              ) : <p className="text-sm text-muted-foreground">Nenhum torneio cadastrado.</p>}
            </div>
            <div className="pt-2 border-t mt-2">
              <Row label="Plano de saúde" value={s.has_health_plan ? "Sim" : "Não"} />
              {s.has_health_plan && (
                <>
                  <Row label="Convênio" value={dash(s.health_plan_name)} />
                  <Row label="Contato" value={dash(s.health_plan_contact)} />
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* CARD 4 — Skin */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><Shirt className="w-4 h-4 text-primary" /> Skin</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-center gap-4">
              <div className="shrink-0">
                <PlayerAvatar config={s.avatar_config} className="w-24 h-24" />
              </div>
              <div className="flex-1">
                <Row label="Número" value={dash(s.jersey_number)} />
                <Row label="Nome na camisa" value={dash(s.jersey_name)} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-end">
        <Button variant="outline" onClick={() => navigate({ to: "/responsavel/alunos" })}>
          Voltar
        </Button>
      </div>
    </div>
  );
}
